import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";
const MAX_QUANTITY = 99;

const getStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const sanitizeProductId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const sanitizeOption = (value) =>
  typeof value === "string" ? value.trim() : "";

const sanitizeQuantity = (value) => {
  const quantity = Number(value);

  if (!Number.isFinite(quantity)) {
    return 1;
  }

  return Math.min(MAX_QUANTITY, Math.max(1, Math.floor(quantity)));
};

const sanitizeCartItems = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce((items, item) => {
    if (!item || typeof item !== "object") {
      return items;
    }

    const id = sanitizeProductId(item.id);

    if (id === null) {
      return items;
    }

    const size = sanitizeOption(item.size);
    const color = sanitizeOption(item.color);
    const quantity = sanitizeQuantity(item.quantity);
    const existingItem = items.find(
      (candidate) =>
        candidate.id === id &&
        candidate.size === size &&
        candidate.color === color
    );

    if (existingItem) {
      existingItem.quantity = Math.min(
        MAX_QUANTITY,
        existingItem.quantity + quantity
      );
    } else {
      items.push({ id, size, color, quantity });
    }

    return items;
  }, []);
};

const sanitizeWishlistItems = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce((ids, item) => {
    const rawId = item && typeof item === "object" ? item.id : item;
    const id = sanitizeProductId(rawId);

    if (id !== null && !ids.includes(id)) {
      ids.push(id);
    }

    return ids;
  }, []);
};

const readStoredItems = (key, sanitizer) => {
  const storage = getStorage();

  if (!storage) {
    return [];
  }

  try {
    const storedValue = storage.getItem(key);
    return storedValue ? sanitizer(JSON.parse(storedValue)) : [];
  } catch {
    return [];
  }
};

const persistItems = (key, value) => {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage may be disabled or full; in-memory state remains available.
  }
};

export const CartContext = createContext({
  cartItems: [],
  wishlistItems: [],
  isCartOpen: false,
  openCart: () => {},
  closeCart: () => {},
  toggleCart: () => {},
});

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredItems(CART_STORAGE_KEY, sanitizeCartItems)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredItems(WISHLIST_STORAGE_KEY, sanitizeWishlistItems)
  );
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    persistItems(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistItems(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  const addToCart = useCallback((productId, quantity = 1, size, color) => {
    const id = sanitizeProductId(productId);

    if (id === null) {
      return;
    }

    const cleanSize = sanitizeOption(size);
    const cleanColor = sanitizeOption(color);
    const cleanQuantity = sanitizeQuantity(quantity);

    setCartItems((previousItems) => {
      const existingItem = previousItems.find(
        (item) =>
          item.id === id &&
          item.size === cleanSize &&
          item.color === cleanColor
      );

      if (!existingItem) {
        return [
          ...previousItems,
          {
            id,
            size: cleanSize,
            color: cleanColor,
            quantity: cleanQuantity,
          },
        ];
      }

      return previousItems.map((item) =>
        item.id === id &&
        item.size === cleanSize &&
        item.color === cleanColor
          ? {
              ...item,
              quantity: Math.min(
                MAX_QUANTITY,
                item.quantity + cleanQuantity
              ),
            }
          : item
      );
    });
    setIsCartOpen(true);
  }, []);

  const removeFromCart = useCallback((productId, size, color) => {
    const id = sanitizeProductId(productId);

    if (id === null) {
      return;
    }

    const cleanSize = sanitizeOption(size);
    const cleanColor = sanitizeOption(color);

    setCartItems((previousItems) =>
      previousItems.filter(
        (item) =>
          !(
            item.id === id &&
            item.size === cleanSize &&
            item.color === cleanColor
          )
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, size, color, quantity) => {
    const id = sanitizeProductId(productId);

    if (id === null) {
      return;
    }

    const cleanSize = sanitizeOption(size);
    const cleanColor = sanitizeOption(color);
    const cleanQuantity = sanitizeQuantity(quantity);

    setCartItems((previousItems) =>
      previousItems.map((item) =>
        item.id === id &&
        item.size === cleanSize &&
        item.color === cleanColor
          ? { ...item, quantity: cleanQuantity }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
    setIsCartOpen(false);
  }, []);

  const openCart = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  const toggleCart = useCallback(() => {
    setIsCartOpen((isOpen) => !isOpen);
  }, []);

  const addToWishlist = useCallback((productId) => {
    const id = sanitizeProductId(productId);

    if (id === null) {
      return;
    }

    setWishlistItems((previousItems) =>
      previousItems.includes(id) ? previousItems : [...previousItems, id]
    );
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    const id = sanitizeProductId(productId);

    if (id === null) {
      return;
    }

    setWishlistItems((previousItems) =>
      previousItems.filter((itemId) => itemId !== id)
    );
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const id = sanitizeProductId(productId);

    if (id === null) {
      return;
    }

    setWishlistItems((previousItems) =>
      previousItems.includes(id)
        ? previousItems.filter((itemId) => itemId !== id)
        : [...previousItems, id]
    );
  }, []);

  const isWishlisted = useCallback(
    (productId) => {
      const id = sanitizeProductId(productId);
      return id !== null && wishlistItems.includes(id);
    },
    [wishlistItems]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      isCartOpen,
      addToCart,
      removeFromCart,
      clearCart,
      updateQuantity,
      openCart,
      closeCart,
      toggleCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted,
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      closeCart,
      isWishlisted,
      isCartOpen,
      openCart,
      removeFromCart,
      removeFromWishlist,
      toggleCart,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
